// Nederlands — de standaardtaal. Dezelfde sleutels als en.ts; de typecheck
// faalt als er een ontbreekt of bijkomt.

import type { MessageKey, Message, Vars } from './en.ts';
import { documentsNl } from './documents.nl.ts';
import { sourceNl } from './source.nl.ts';
import { statesNl } from './states.nl.ts';
import { messagesNl } from './messages.nl.ts';
import { queueNl } from './queue.nl.ts';

const n = (vars: Vars, name = 'count'): number => Number(vars[name] ?? 0);

export const nl: Readonly<Record<MessageKey, Message>> = {
  ...documentsNl,
  ...sourceNl,
  ...statesNl,
  ...messagesNl,
  ...queueNl,
  // --- gedeeld ----------------------------------------------------------------
  'common.cancel': 'Annuleren',
  'common.close': 'Sluiten',
  'common.reading': 'Bezig met lezen…',
  'common.moreInfo': 'Meer uitleg',
  'common.viewAll': 'Alles bekijken',
  'common.view': 'Bekijken',
  'common.estimate': 'Schatting',
  'common.thisMonth': 'Deze maand',
  'common.notReadable': 'Dit kon niet worden gelezen.',
  'common.hours': (v: Vars) => (n(v) === 1 ? 'uur' : 'uur'),
  'common.days': (v: Vars) => (n(v) === 1 ? 'dag' : 'dagen'),

  // --- de schil ---------------------------------------------------------------
  'nav.today': 'Vandaag',
  'nav.work': 'Te beoordelen',
  'nav.administrations': 'Administraties',
  'nav.documents': 'Documenten',
  'nav.bank': 'Bankafletteren',
  'nav.messages': 'Klantberichten',
  'nav.rules': 'Werkregels',
  'nav.connections': 'Koppelingen',
  'nav.activity': 'Activiteit',
  'nav.ask': 'Vraag Fleur',
  'nav.agency-offices': 'Kantoren',
  'nav.agency-onboarding': 'Onboarding',
  'nav.agency-workspace': 'Kantoorwerkplek',
  'nav.agency-usage': 'Kosten & gebruik',
  'nav.group.workspace': 'Werkplek',
  'nav.group.assistant': 'Assistent',
  'nav.group.agency': 'Bureau',
  'nav.group.manage': 'Beheer',
  'nav.main': 'Hoofdnavigatie',
  'nav.assistantNav': 'Assistent-navigatie',
  'nav.agencyNav': 'Bureau-navigatie',
  'nav.waitingBadge': (v: Vars) => `${n(v)} wachtend`,
  'shell.byline': 'door Agency AI',
  'shell.officeWorkspace': 'Kantoorwerkplek',
  'shell.office': 'Kantoor',
  'shell.signOut': 'Uitloggen',
  'shell.acrossOffices': 'Over de kantoren waar je toegang toe hebt',
  'shell.selectAdministration': 'Kies administratie',
  'shell.allAdministrations': 'Alle administraties',
  'shell.language': 'Taal',
  'role.owner': 'Eigenaar',
  'role.reviewer': 'Beoordelaar',
  'role.viewer': 'Meekijker',
  'role.agency_operator': 'Bureaumedewerker',

  'search.placeholder': 'Zoek documenten, leveranciers, bedragen',
  'search.label': 'Zoek documenten, leveranciers en bedragen',
  'search.results': 'Zoekresultaten',
  'search.searching': 'Bezig met zoeken…',
  'search.unreachable': 'De dienst was niet bereikbaar.',
  'search.failed': 'De zoekopdracht kon niet worden uitgevoerd.',
  'search.none': (v: Vars) => `Geen document past bij “${v['text']}” in de administraties die je kunt zien.`,
  'search.notAmount': ' De tekst is niet als bedrag gelezen: er is meer dan één lezing mogelijk, of geen.',
  'search.alsoAmount': (v: Vars) => `Ook vergeleken als het bedrag ${v['amount']}.`,
  'search.noInvoiceNumber': 'Geen factuurnummer',
  'search.matchedOn': (v: Vars) => `gevonden op ${v['field']}`,
  'search.field.invoice_number': 'factuurnummer',
  'search.field.supplier': 'leverancier',
  'search.field.amount': 'bedrag',
  'search.administration': 'Administratie',

  // --- Vandaag ----------------------------------------------------------------
  'today.greeting.morning': 'Goedemorgen',
  'today.greeting.afternoon': 'Goedemiddag',
  'today.greeting.evening': 'Goedenavond',
  'today.waitingLine': (v: Vars) =>
    n(v) === 0
      ? 'Er wacht niets op je akkoord.'
      : `Er ${n(v) === 1 ? 'wacht 1 item' : `wachten ${n(v)} items`} op je akkoord.`,
  'today.upload': 'Document uploaden',
  'today.kpi.waiting': 'Wacht op jouw akkoord',
  'today.kpi.waitingHint': 'Openen in de werkvoorraad',
  'today.kpi.booked': 'Geboekt deze maand',
  'today.kpi.bookedHint': 'Bevestigd door het boekhoudpakket',
  'today.kpi.saved': 'Tijd bespaard',
  'today.kpi.savedHint': 'Zo is het berekend',
  'today.kpi.vat': 'Tot de btw-aangifte',
  'today.kpi.vatHint': (v: Vars) => `${v['label']} · uiterlijk ${v['due']}`,
  'today.kpi.unavailable': 'Niet beschikbaar',
  'today.waiting.title': 'Wacht op jouw akkoord',
  'today.waiting.empty': 'Er wacht niets op jouw akkoord.',
  'today.recent.title': 'Net gebeurd',
  'today.recent.all': 'Alle activiteit',
  'today.recent.empty': 'Er is in deze selectie nog niets gebeurd.',
  'today.spend.title': (v: Vars) => `Uitgaven in ${v['month']}`,
  'today.spend.sub': 'Netto, bevestigde boekingen',
  'today.spend.exVat': 'excl. btw',
  'today.spend.empty': 'Er is deze maand nog geen inkoop geboekt.',
  'today.spend.other': 'Overig',
  'today.spend.ledger': (v: Vars) => `Grootboek ${v['code']}`,
  'today.spend.notSynced': (v: Vars) => `Grootboekref. ${v['ref']} (nog niet gesynchroniseerd)`,
  'today.spend.currencies': (v: Vars) => `${n(v)} valuta`,
  'today.approved.title': 'Voorstellen zonder aanpassing',
  'today.approved.empty': 'Er is deze maand nog geen boeking goedgekeurd.',
  'today.approved.of': (v: Vars) =>
    `${v['unchanged']} van de ${v['total']} ${Number(v['total']) === 1 ? 'voorstel' : 'voorstellen'} goedgekeurd zoals de AI ${Number(v['total']) === 1 ? 'het' : 'ze'} klaarzette.`,
  'today.approved.changed': (v: Vars) => `${v['changed']} aangepast vóór akkoord`,
  'today.approved.info': 'Voorstellen die precies zo zijn goedgekeurd als je AI ze voorbereidde, zonder dat iemand ze eerst aanpaste.',
  'today.vat.title': 'Btw-aangifte',
  'today.vat.daysLeft': (v: Vars) => `${n(v) === 1 ? 'dag' : 'dagen'} te gaan`,
  'today.vat.period': (v: Vars) => `${v['label']} · ${v['from']} – ${v['to']} · uiterlijk ${v['due']}`,
  'today.vat.confirmed': (v: Vars) => `${v['confirmed']} van ${v['counted']} documenten geboekt`,
  'today.vat.open': (v: Vars) => `${v['open']} nog open`,
  'today.vat.noneDated': (v: Vars) => `Er is nog geen document gedateerd in ${v['label']}.`,
  'today.vat.undated': (v: Vars) =>
    `${n(v)} ${n(v) === 1 ? 'ander document is' : 'andere documenten zijn'} nog niet gelezen en ${n(v) === 1 ? 'heeft' : 'hebben'} geen factuurdatum, dus ${n(v) === 1 ? 'telt' : 'tellen'} nog niet mee in een periode.`,
  'today.vat.details': 'Btw-details',
  'today.vat.bookedLabel': 'Documenten in deze periode, geboekt',
  'today.vat.openLabel': 'Documenten in deze periode, nog open',
  'today.saved.dialogTitle': 'Zo berekenen we de bespaarde tijd',
  'today.saved.formula': 'Documenten geboekt deze maand × jouw minuten handwerk, min de beoordelingstijd die we hebben gemeten.',
  'today.saved.documents': 'Documenten geboekt deze maand',
  'today.saved.perDocument': 'Minuten handwerk per document (het getal van jouw kantoor)',
  'today.saved.manual': 'Met de hand boeken',
  'today.saved.measured': (v: Vars) => `Gemeten beoordeling (${v['measured']} van ${v['documents']} documenten)`,
  'today.saved.result': 'Tijd bespaard',
  'today.saved.cap': (v: Vars) =>
    `Een beoordeling meten we van het openen van de bron tot het akkoord, tot maximaal ${v['cap']} minuten. Een niet-gemeten beoordeling telt als nul.`,
  'today.saved.none': 'Er is deze maand nog geen boeking bevestigd, dus nog geen tijd bespaard.',
  'today.minutes': (v: Vars) => `${v['value']} min`,
  'today.hoursShort': (v: Vars) => `${v['value']} u`,

  'today.toWork': 'Naar de werkvoorraad',
  'today.waiting.settles': 'zou deze factuur afletteren',
  'today.waiting.paymentFound': (v: Vars) => `Betaling gevonden voor factuur ${v['invoice']}`,
  'today.waiting.check': 'Controleren',
  'today.kpi.waitingTag': 'Nu doen',
  'today.duration': (v: Vars) =>
    n(v, 'hours') === 0 ? `${n(v, 'minutes')} m` : n(v, 'minutes') === 0 ? `${n(v, 'hours')} u` : `${n(v, 'hours')} u ${n(v, 'minutes')} m`,
  'today.recent.live': 'Live',
  'today.vat.due': (v: Vars) => `uiterlijk ${v['due']}`,
  'today.vat.progress': (v: Vars) =>
    `${v['confirmed']} van ${v['counted']} documenten geboekt, ${v['open']} ${Number(v['open']) === 1 ? 'staat' : 'staan'} nog open.`,
  'work.kind.document': 'Document',
  'work.kind.proposal': 'Boekingsvoorstel',
  'work.kind.operation': 'Boeking onderweg',
  'work.kind.bank_match': 'Banktransactie',
  'work.kind.request': 'Klantverzoek',
  'work.docType.invoice': 'Factuur',
  'work.docType.credit_note': 'Creditnota',
  'work.docType.receipt': 'Bon',
  'work.docType.bank_statement': 'Bankafschrift',
  'time.justNow': 'zojuist',

  // --- "Net gebeurd" ----------------------------------------------------------
  'feed.extraction.completed': 'Factuur gelezen, boeking klaargezet',
  'feed.extraction.blocked': 'Factuur gelezen, er moet iets gecontroleerd worden',
  'feed.extraction.abandoned': 'Factuur kon niet gelezen worden',
  'feed.proposal.revised': 'Boeking aangepast',
  'feed.proposal.approved': 'Boeking goedgekeurd',
  'feed.document.excluded': 'Document uitgesloten',
  'feed.bank_match.approved': 'Banktransactie afgeletterd',
  'feed.operation.submitted': (v: Vars) => `Boeking verstuurd naar ${v['package']}`,
  'feed.operation.confirmed': (v: Vars) => `Geboekt in ${v['package']}`,
  'feed.operation.failed_safe': 'Boeking gestopt, er is niets veranderd',
  'feed.operation.unknown': 'Nog niet zeker of de boeking is aangekomen, wordt gecontroleerd',
  'feed.operation.unknown_after_crash': 'Nog niet zeker of de boeking is aangekomen, wordt gecontroleerd',
  'feed.operation.duplicate_present': (v: Vars) => `Boeking stond al in ${v['package']}`,
  'feed.operation.needs_review': 'Boeking moet met de hand gecontroleerd worden',
  'feed.operation.probe_requested': 'Status opgevraagd bij het boekhoudpakket',
  'feed.operation.retry_authorised': 'Opnieuw versturen toegestaan',
  'feed.statement.imported': 'Bankafschrift ingelezen',
  'feed.statement.blocked': 'Bankafschrift moet gecontroleerd worden',
  'feed.statement.abandoned': 'Bankafschrift kon niet gelezen worden',
  'feed.connection.sync_succeeded': (v: Vars) => `Gesynchroniseerd met ${v['package']}`,
  'feed.connection.sync_failed': (v: Vars) => `Synchronisatie met ${v['package']} mislukt`,
  'feed.counterparty.classified': 'Leverancier ingedeeld',
  'feed.document.source_url_issued': 'Document geopend',
  'feed.document.source_downloaded': 'Document geopend',
  'feed.other': 'Vastgelegd',
  'feed.package': 'het boekhoudpakket',

  // --- Documenten: geboekt ----------------------------------------------------
  'documents.view.open': 'Open',
  'documents.view.booked': 'Geboekt deze maand',
  'documents.booked.empty': 'Er is deze maand nog niets geboekt.',
  'documents.booked.more': (v: Vars) => `De laatste ${v['shown']} van ${v['total']}.`,

  // --- Uploaden ---------------------------------------------------------------
  'upload.title': 'Document uploaden',
  'upload.drop': 'Sleep hier een pdf, foto of MT940 naartoe',
  'upload.kinds': 'Pdf, JPEG, PNG, platte tekst of een MT940-bankafschrift, op een goedgekeurde uploadroute van dit kantoor',
  'upload.noRoute': 'Er is voor dit kantoor geen uploadroute goedgekeurd. Een eigenaar voegt die toe onder Werkregels.',
  'upload.route': 'Route',
  'upload.routeLabel': 'Uploadroute',
  'upload.fileLabel': 'Documentbestand',
  'upload.button': 'Uploaden',
  'upload.busy': 'Bezig met uploaden…',
  'upload.tooLarge': (v: Vars) => `Dit bestand is ${v['size']}; de limiet is ${v['limit']}.`,
  'upload.held': (v: Vars) => `Ontvangen en tegengehouden: ${v['blockers']}. Er wordt niets uit gelezen.`,
  'upload.received': 'Ontvangen. Het wordt op de achtergrond gelezen; de status laat zien hoe ver het is.',
  'upload.failed': 'Het uploaden is mislukt.',
  'upload.tooLargeApi': 'Het bestand is groter dan de API accepteert.',
  'upload.unreachable': 'De dienst was niet bereikbaar. Er is niets geüpload.',
  'upload.refused': (v: Vars) => `Geweigerd (${v['code']})${v['fields'] === '' ? '' : ` — ${v['fields']}`}.`,

  // --- Werkregels -------------------------------------------------------------
  'rules.title': 'Werkregels',
  'rules.intro': 'Wat jouw kantoor vastlegt, zodat de AI het niet steeds hoeft te vragen.',
  'rules.introInfo':
    'Wie de tegenpartijen zijn, op welke grootboekrekening een leverancier wordt geboekt, waar documenten binnenkomen en wat ooit zonder mens mag gebeuren. Elke knop hier legt een besluit vast met naam en tijd; niets is een schakelaar.',
  'rules.section.notAvailable': 'Dit is niet beschikbaar voor jouw sessie.',
  'rules.section.roleCannotRead': 'Jouw rol mag dit niet inzien.',
  'rules.section.unreachable': 'De dienst was niet bereikbaar.',
  'rules.action.failed': 'Het verzoek kon niet worden uitgevoerd.',
  'rules.action.stale': 'Dit is veranderd sinds het scherm werd geladen, dus er is niets vastgelegd. Laad opnieuw en kijk wat er nu staat.',
  'rules.action.forbidden': 'Jouw rol mag dit niet doen.',
  'rules.action.gone': 'Dat is er niet meer — misschien heeft iemand het al afgehandeld.',
  'rules.action.refused': (v: Vars) => `Het verzoek is geweigerd: ${v['fields']}.`,
  'rules.action.validation': 'controle mislukt',

  // Boekingsregels
  'rules.booking.title': 'Boekingsregels',
  'rules.booking.sub': 'Op welke grootboekrekening elke leverancier wordt geboekt',
  'rules.booking.info':
    'Een regel wordt opgeslagen als een beoordelaar een voorgestelde leverancier op een document bevestigt. Hier alleen inzien: je voegt een regel toe vanaf het document waarvoor hij nodig is.',
  'rules.booking.reading': 'Boekingsregels lezen…',
  'rules.booking.empty': 'Er is nog geen leverancier met een boekingsregel.',
  'rules.booking.openDocuments': 'Naar documenten',
  'rules.booking.notConfigured': (v: Vars) => `${v['name']}: er zijn nog geen boekingsregels ingesteld.`,
  'rules.booking.supplier': 'Leverancier',
  'rules.booking.matchedOn': 'Herkend aan',
  'rules.booking.ledger': 'Grootboekrekening',
  'rules.booking.vat': 'Btw',
  'rules.booking.default': (v: Vars) => `Altijd op ${v['ledger']} (standaard)`,
  'rules.booking.always': (v: Vars) => `Altijd op ${v['ledger']}`,
  'rules.booking.showAll': (v: Vars) => `Alle ${v['count']} tonen`,
  'rules.booking.showLess': 'Minder tonen',
  'rules.booking.search': 'Zoek leverancier',
  'rules.booking.noMatch': (v: Vars) => `Geen leverancier gevonden voor “${v['text']}”.`,
  'rules.routes.more': (v: Vars) => `Meer voor ${v['name']}`,
  'rules.routes.uploadTitle': 'Uploaden in het dashboard',
  'rules.bank.title': 'Bank en kas',
  'rules.bank.info': 'Hoe banktransacties in het boekhoudpakket komen, op welke grootboekrekening contante bonnen worden betaald en waar een verrekende creditnota op komt. Te wijzigen bij Administraties, door de eigenaar of het bureau.',
  'rules.bank.reading': 'Instellingen lezen…',
  'rules.bank.delivery': 'Bankgegevens',
  'rules.bank.cash': 'Contante bonnen',
  'rules.bank.settlement': 'Creditnota verrekenen',
  'rules.bank.liveFeed': (v: Vars) => `Via ${v['package']}`,
  'rules.bank.defaultLiveFeed': (v: Vars) => `Via ${v['package']} (standaard)`,
  'rules.bank.mt940': 'MT940 upload',
  'rules.bank.notSet': 'Nog niet ingesteld',
  'rules.routes.approvedOn': (v: Vars) => `Goedgekeurd op ${v['day']}`,
  'rules.routes.active': 'Actief',
  'rules.routes.mailTitle': 'Doorsturen per e-mail',
  'rules.routes.mailUnset': 'Nog geen adres ingesteld',
  'rules.routes.setUp': 'Instellen',
  'rules.auto.offTitle': 'Automatisch boeken staat uit',
  'rules.auto.offLine': 'Elke boeking wacht op een akkoord van jou of je collega.',
  'rules.auto.onTitle': 'Automatisch boeken staat aan',
  'rules.auto.onLine': 'Wat vrijgegeven is, wordt zonder akkoord geboekt.',
  'rules.booking.defaultUnknown': 'Standaard kostenrekening',
  'rules.booking.vatNumber': (v: Vars) => `Btw ${v['value']}`,
  'rules.booking.kvk': (v: Vars) => `KvK ${v['value']}`,
  'rules.booking.name': (v: Vars) => `Naam “${v['value']}”`,
  'rules.booking.deductible': 'Aftrekbaar',
  'rules.booking.notDeductible': 'Niet aftrekbaar',
  'rules.booking.oneOff': 'Eenmalige partij',
  'rules.booking.unnamedRelation': (v: Vars) => `Relatie ${v['id']}`,
  'rules.booking.notSynced': (v: Vars) => `Grootboek ${v['id']} (nog niet gesynchroniseerd)`,

  // Tegenpartijen
  'rules.cp.title': 'Leveranciers om te controleren',
  'rules.cp.sub': 'Partijen die we op papier niet konden onderscheiden',
  'rules.cp.info': 'Partijen die we alleen op papier niet uit elkaar konden houden, en wat dit kantoor over ze heeft besloten.',
  'rules.cp.reading': 'Vragen over tegenpartijen lezen…',
  'rules.cp.empty': 'Alles is duidelijk',
  'rules.cp.emptyLine': 'Kan de AI een leverancier niet herkennen, dan zie je hem hier.',
  'rules.cp.emptyInfo':
    'Elke partij op een document had tot nu toe genoeg om het te zien: een btw-nummer, een KvK-nummer, een bestaande relatie of een tweede factuur van dezelfde partij.',
  'rules.cp.noName': 'Een partij zonder leesbare naam',
  'rules.cp.arrived': (v: Vars) => `${v['administration']} · binnengekomen ${v['day']}`,
  'rules.cp.noNumbers': 'geen btw- of KvK-nummer op het document',
  'rules.cp.numbers': (v: Vars) => `Btw ${v['vat']} · KvK ${v['kvk']}`,
  'rules.cp.open': 'Document openen',
  'rules.cp.answer': 'Beantwoorden',
  'rules.cp.answered': (v: Vars) =>
    `Beantwoord als ${v['kind'] === 'one_off' ? 'eenmalige aankoop' : 'vaste leverancier'}. Wacht tot het document opnieuw is gelezen — je hoeft niets meer te doen.`,
  'rules.cp.answeredBadge': 'Beantwoord',
  'rules.cp.decidedTitle': 'Wat is besloten',
  'rules.cp.decidedNone': 'Er is in dit kantoor nog niets ingedeeld.',
  'rules.cp.decidedInfo':
    'Oudere antwoorden blijven bewaard in plaats van vervangen. Er zijn documenten op geboekt, en “wat dachten we dat deze partij was, en wie zei dat?” moet voor elk daarvan te beantwoorden blijven.',
  'rules.cp.col.party': 'Partij',
  'rules.cp.col.matchedOn': 'Herkend aan',
  'rules.cp.col.decided': 'Besluit',
  'rules.cp.col.by': 'Door',
  'rules.cp.col.when': 'Wanneer',
  'rules.cp.vatNumber': 'Btw-nummer',
  'rules.cp.name': 'Naam',
  'rules.cp.oneOff': 'Eenmalig',
  'rules.cp.recurring': 'Vaste leverancier',
  'rules.cp.superseded': 'vervangen',
  'rules.cp.recordedOneOff':
    'Vastgelegd als eenmalige aankoop. Er is niets blijvends aangemaakt en het document wordt opnieuw gelezen.',
  'rules.cp.recordedRecurring':
    'Vastgelegd als vaste leverancier. Op welke relatie wordt geboekt moet nog iemand goedkeuren, dus het document wacht daarop.',

  'classify.question': 'Is dit een eenmalige aankoop, of een leverancier waar je vaker koopt?',
  'classify.why':
    'Niets op dit document zegt welke van de twee — geen btw-nummer, geen KvK-nummer, en we hebben deze partij nog niet eerder gezien. Je antwoord wordt vastgelegd als besluit, met je naam en de tijd.',
  'classify.oneOff': 'Eenmalige aankoop',
  'classify.oneOffDetail': '— geboekt op de eenmalige tegenpartij van deze administratie. Er wordt geen vaste relatie aangemaakt.',
  'classify.notSure': 'Kies dit als je het niet zeker weet.',
  'classify.recurring': 'Vaste leverancier',
  'classify.recurringDetail':
    '— deze partij hoort een eigen relatie te krijgen. Welke relatie dat is moet nog worden goedgekeurd, dus het document wacht daarop.',
  'classify.record': 'Dit besluit vastleggen',

  // Binnenkomst
  'rules.routes.title': 'Waar documenten binnenkomen',
  'rules.routes.sub': 'Elk adres hoort bij één administratie',
  'rules.routes.info': 'Elk adres hoort bij één administratie, en het kantoor keurt die koppeling goed.',
  'rules.routes.add': 'Toevoegen',
  'rules.routes.roleCannot': (v: Vars) => `Jouw rol (${v['role']}) mag geen route goedkeuren.`,
  'rules.routes.administration': 'Administratie',
  'rules.routes.channel': 'Kanaal',
  'rules.routes.address': 'Adres',
  'rules.routes.addressLabel': 'Bronadres',
  'rules.routes.secret': 'Verwijzing naar ondertekeningsgeheim',
  'rules.routes.secretHint': 'De naam waaronder het geheim is opgeslagen — nooit het geheim zelf.',
  'rules.routes.approve': 'Deze route goedkeuren',
  'rules.routes.reading': 'Binnenkomstroutes lezen…',
  'rules.routes.empty': 'Er is nog geen route ingesteld.',
  'rules.routes.emptyInfo':
    'Er is geen adres om op binnen te komen. Een document dat ergens heen wordt gestuurd, gaat naar de routeringswachtrij in plaats van een administratie. Veilig, maar niet handig.',
  'rules.routes.approved': (v: Vars) => `${v['channel']} · ${v['administration']} · goedgekeurd ${v['day']}`,
  'rules.routes.secretOk': ' · ondertekeningsgeheim ingesteld',
  'rules.routes.secretMissing': ' · geen ondertekeningsgeheim, dus leveringen worden geweigerd',
  'rules.routes.revoke': 'Intrekken',
  'rules.routes.revoked': (v: Vars) =>
    `${n(v)} ingetrokken ${n(v) === 1 ? 'route blijft' : 'routes blijven'} bewaard, zodat te zien blijft welke regel een document heeft gerouteerd.`,
  'rules.routes.created': 'De route is goedgekeurd. Documenten op dat adres horen nu bij die administratie.',
  'rules.routes.revokedNotice':
    'De route is ingetrokken en het adres is weer vrij. Wat er nog binnenkomt, gaat naar de routeringswachtrij.',
  'rules.channel.mailbox': 'Mailbox',
  'rules.channel.mailboxDetail': 'Een adres dat we voor één administratie publiceren. Documenten die erheen worden gemaild, komen hier binnen.',
  'rules.channel.upload': 'Upload',
  'rules.channel.uploadDetail': 'Een inbox waar een ingelogde beoordelaar naar uploadt.',
  'rules.channel.webhook': 'Webhook',
  'rules.channel.webhookDetail': 'Een aanbieder post naar een ondertekend endpoint. Vraagt een ondertekeningsgeheim in de geheimenkluis.',

  // Routering
  'rules.triage.title': 'Binnengekomen zonder route',
  'rules.triage.sub': 'Documenten naar een adres zonder route',
  'rules.triage.reading': 'Routeringswachtrij lezen…',
  'rules.triage.notGranted': 'Deze wachtrij is niet aan jou toegewezen.',
  'rules.triage.notGrantedInfo':
    'Een document zonder route hoort nog bij geen enkel kantoor, dus voor deze wachtrij is een aparte toestemming nodig die aan benoemde medewerkers wordt gegeven, niet aan een rol. Vraag het je contactpersoon bij het bureau; er wordt niets van dit kantoor voor je verborgen.',
  'rules.triage.empty': 'Er wacht niets.',
  'rules.triage.emptyInfo': 'Elk binnengekomen document kwam op een adres met een route erachter.',
  'rules.triage.arrived': (v: Vars) => `binnengekomen ${v['when']}`,
  'rules.triage.fileInto': 'Archiveren in',
  'rules.triage.fileIntoLabel': (v: Vars) => `${v['address']} archiveren in`,
  'rules.triage.dismissReason': 'Of afwijzen, met een reden',
  'rules.triage.dismissLabel': (v: Vars) => `${v['address']} afwijzen omdat`,
  'rules.triage.file': 'Archiveren',
  'rules.triage.dismiss': 'Afwijzen',
  'rules.triage.readOnly': 'alleen lezen',
  'rules.triage.roleCannot': (v: Vars) => `Jouw rol (${v['role']}) mag geen binnenkomst archiveren.`,
  'rules.triage.foot':
    'Je beslist over het adres waar een document heen is gestuurd, niet over de inhoud. Het document wordt hier niet getoond en kan dat ook niet: zonder kantoor zijn er geen beoordelaars die het mogen lezen. Is het adres niet genoeg, vraag dan de klant van wie het is.',
  'rules.triage.filed': 'Gearchiveerd. Het document staat in die administratie en wordt gelezen zoals elk ander document.',
  'rules.triage.dismissed': 'Afgewezen. Het document blijft bewaard met jouw reden en staat niet meer in de wachtrij.',
  'rules.unrouted.no_route': 'Er is niets ingesteld voor het adres waar het heen is gestuurd.',
  'rules.unrouted.route_revoked': 'De route voor dat adres is ingetrokken.',
  'rules.unrouted.ambiguous_source': 'Het kwam binnen op twee adressen van twee verschillende administraties.',

  // Automatisering
  'rules.auto.title': 'Automatisering',
  'rules.auto.sub': 'Eén onderdeel tegelijk: getest, goedgekeurd, vrijgegeven',
  'rules.auto.info': 'Eén onderdeel tegelijk, elk getest, goedgekeurd door dit kantoor en apart vrijgegeven.',
  'rules.auto.request': 'Aanvragen',
  'rules.auto.lockTitle': 'Hier gebeurt nog niets vanzelf.',
  'rules.auto.lockInfo':
    'Een vrijgegeven onderdeel is een vastgelegde toestemming met bewijs en een akkoord erachter, en geen enkel deel van dit systeem handelt erop: elk document en elke aflettering wacht nog steeds op een mens. Als dat verandert, staat dat hier bij de vrijgave, in plaats van dat deze regel stilletjes verdwijnt.',
  'rules.auto.scope': 'Onderdeel',
  'rules.auto.amountLimit': 'Bedraglimiet (EUR)',
  'rules.auto.amountLimitLabel': 'Bedraglimiet',
  'rules.auto.record': 'Deze aanvraag vastleggen',
  'rules.auto.recorded':
    'Vastgelegd. Het kan niet worden gewijzigd — een wijziging is een nieuwe versie — en er gebeurt niets tot het is getest, goedgekeurd en vrijgegeven.',
  'rules.auto.reading': 'Automatiseringsbeleid lezen…',
  'rules.auto.empty': 'Er is niets geautomatiseerd en niets aangevraagd.',
  'rules.auto.emptyInfo':
    'Elk document wordt voorbereid en wacht op een mens. Daar begint een nieuwe administratie en daar blijft hij tot een onderdeel is getest en goedgekeurd.',
  'rules.auto.version': (v: Vars) => `${v['scope']} · versie ${v['version']}`,
  'rules.auto.askedFor': (v: Vars) => `${v['administration']} · aangevraagd ${v['day']} · beleid ${v['hash']}…`,
  'rules.auto.tested': 'Getest',
  'rules.auto.notYet': 'Nog niet.',
  'rules.auto.cases': (v: Vars) => `${v['passed']} van ${v['total']} gevallen`,
  'rules.auto.approvedBy': 'Goedgekeurd door dit kantoor',
  'rules.auto.notApproved': 'Niet voor de laatst vastgelegde resultaten.',
  'rules.auto.released': 'Vrijgegeven',
  'rules.auto.no': 'Nee.',
  'rules.auto.boundTo': (v: Vars) => `${v['day']} · gekoppeld aan ${v['versions']}.`,
  'rules.auto.and': (v: Vars) => `${v['a']} en ${v['b']}`,
  'rules.auto.inForce': 'Van kracht.',
  'rules.auto.notInForce': 'Het staat nog niets toe — elk item wacht nog op een mens.',
  'rules.auto.connectorVersion': 'Connectorversie',
  'rules.auto.capabilityEvidence': 'Bewijs van mogelijkheden',
  'rules.auto.capabilityEvidenceLabel': 'Verwijzing naar bewijs van mogelijkheden',
  'rules.auto.modelVersion': 'Modelversie (indien van toepassing)',
  'rules.auto.modelVersionLabel': 'Modelversie',
  'rules.auto.dataset': 'Testset',
  'rules.auto.datasetLabel': 'Verwijzing naar testset',
  'rules.auto.results': 'Testresultaten',
  'rules.auto.resultsLabel': 'Verwijzing naar testresultaten',
  'rules.auto.casesRun': 'Gevallen gedraaid',
  'rules.auto.casesPassed': 'Gevallen geslaagd',
  'rules.auto.recordEvidence': 'Bewijs vastleggen',
  'rules.auto.evidenceRecorded':
    'Vastgelegd. Het kantoor moet precies deze resultaten goedkeuren voordat er iets op kan worden vrijgegeven.',
  'rules.auto.recordTestEvidence': 'Testbewijs vastleggen',
  'rules.auto.approveResults': 'Deze resultaten goedkeuren',
  'rules.auto.approvedNotice': 'Goedgekeurd, gekoppeld aan precies deze resultaten. Bij opnieuw testen vragen we het je opnieuw.',
  'rules.auto.release': 'Vrijgeven',
  'rules.auto.releasedNotice': 'Vrijgegeven, gekoppeld aan de geteste versies. Het staat op zichzelf nog niets toe.',
  'rules.scope.standard_purchase_booking': 'Standaard inkoopboeking',
  'rules.scope.standard_purchase_booking.detail':
    'Gewone inkoopfacturen met een gekoppelde leverancier, één valuta en geen creditnota’s.',
  'rules.scope.standard_sales_booking': 'Standaard verkoopboeking',
  'rules.scope.standard_sales_booking.detail': 'Gewone verkoopfacturen vanuit deze administratie.',
  'rules.scope.exact_bank_matching': 'Exact afletteren',
  'rules.scope.exact_bank_matching.detail': 'Bankregels die precies op één openstaande post passen. Geen deelbetalingen, geen restbedragen.',
  'rules.scope.standard_missing_document_requests': 'Standaard verzoeken om ontbrekende documenten',
  'rules.scope.standard_missing_document_requests.detail': 'Een klant vragen om een bon waarvan we zien dat hij ontbreekt.',
  'rules.gate.test_evidence': 'Wacht op testbewijs',
  'rules.gate.test_evidence.who': 'Je bureaumedewerker draait de tests en legt de resultaten vast.',
  'rules.gate.office_approval': 'Wacht op jouw akkoord',
  'rules.gate.office_approval.who': 'De eigenaar van het kantoor keurt precies de vastgelegde resultaten goed.',
  'rules.gate.release': 'Wacht op vrijgave',
  'rules.gate.release.who': 'Je bureaumedewerker geeft het vrij en koppelt het aan de geteste versies.',
  'rules.gate.released': 'Vrijgegeven',
  'rules.gate.released.who': 'Vastgelegd en gekoppeld aan het bewijs en het akkoord waarop het is gegeven.',
};
